import React, { useState, useEffect, useRef } from "react";
import { Alert, Box, Stack, TextField, Typography } from "@mui/material";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";

import FormModal from "../../component/common/FormModal";
import FormField from "../../component/common/FormField";
import ActionButton from "../../component/common/ActionButton";
import { ds } from "../../styles/designSystem";
import { feedbackApi } from "./api/feedbackApi";
import { notify } from "../../util/toast";
import FeedbackAttachmentList, { PendingFileChip } from "./FeedbackAttachments";

const C = ds.colors;

/**
 * 피드백 작성/수정 모달. 이미지·파일 첨부 지원.
 * @param editTarget 값이 있으면 수정 모드(id/title/content/attachments), 없으면 작성 모드.
 */
export default function FeedbackComposeModal({ open, onClose, onSaved, editTarget }) {
    const isEdit = Boolean(editTarget);
    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [pendingFiles, setPendingFiles] = useState([]);   // 아직 업로드 안 한 File[]
    const [existing, setExisting] = useState([]);           // 수정 모드: 서버에 이미 있는 첨부
    const [saving, setSaving] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setTitle(editTarget?.title || "");
            setContent(editTarget?.content || "");
            setExisting(editTarget?.attachments || []);
            setPendingFiles([]);
        }
    }, [open, editTarget]);

    const pickFiles = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length) setPendingFiles((prev) => [...prev, ...files]);
        e.target.value = ""; // 같은 파일 다시 선택 가능하게 초기화
    };

    const removePending = (idx) =>
        setPendingFiles((prev) => prev.filter((_, i) => i !== idx));

    const removeExisting = async (attId) => {
        try {
            await feedbackApi.deleteAttachment(attId);
            setExisting((prev) => prev.filter((a) => a.id !== attId));
        } catch (e) {
            notify(e?.message || "첨부 삭제에 실패했습니다.", "error");
        }
    };

    const handleSave = async () => {
        if (!content.trim()) {
            notify("내용을 입력해주세요.", "warning");
            return;
        }
        setSaving(true);
        try {
            const payload = { title: title.trim() || null, content: content.trim() };
            let feedbackId;
            if (isEdit) {
                await feedbackApi.update(editTarget.id, payload);
                feedbackId = editTarget.id;
            } else {
                const res = await feedbackApi.create(payload);
                const created = res?.data ?? res;
                feedbackId = created?.id;
            }

            // 첨부 업로드 (best-effort — 일부 실패해도 글 자체는 저장됨)
            if (feedbackId && pendingFiles.length) {
                for (const f of pendingFiles) {
                    try {
                        await feedbackApi.uploadAttachment(feedbackId, f);
                    } catch (e) {
                        notify(`${f.name} 업로드 실패`, "error");
                    }
                }
            }

            notify(isEdit ? "수정되었습니다." : "등록되었습니다.", "success");
            onSaved?.();
            onClose?.();
        } catch (e) {
            notify(e?.message || "저장에 실패했습니다.", "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <FormModal
            open={open}
            onClose={onClose}
            title={isEdit ? "피드백 수정" : "새 피드백 작성"}
            subtitle="익명으로 등록됩니다"
            maxWidth="sm"
            onSave={handleSave}
            saving={saving}
            saveLabel={isEdit ? "수정" : "등록"}
            saveDisabled={!content.trim()}
        >
            {!isEdit && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    작성자는 <b>공개되지 않습니다.</b> 자유롭게 의견을 남겨주세요.
                </Alert>
            )}
            <FormField label="제목 (선택)">
                <TextField
                    fullWidth
                    size="small"
                    placeholder="제목을 입력하세요"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    inputProps={{ maxLength: 200 }}
                />
            </FormField>
            <FormField label="내용" required>
                <TextField
                    fullWidth
                    multiline
                    minRows={5}
                    maxRows={14}
                    placeholder="피드백 내용을 입력하세요"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                />
            </FormField>

            {/* 첨부 */}
            <FormField label="첨부 (이미지·파일)">
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    onChange={pickFiles}
                />
                <ActionButton
                    variant="secondary"
                    label="파일 첨부"
                    startIcon={<AttachFileRoundedIcon sx={{ fontSize: 16 }} />}
                    onClick={() => fileInputRef.current?.click()}
                />

                {/* 기존 첨부 (수정 모드) */}
                {existing.length > 0 && (
                    <FeedbackAttachmentList attachments={existing} editable onDelete={removeExisting} />
                )}

                {/* 업로드 대기 파일 */}
                {pendingFiles.length > 0 && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", rowGap: 1 }}>
                        {pendingFiles.map((f, i) => (
                            <PendingFileChip key={`${f.name}-${i}`} file={f} onRemove={() => removePending(i)} />
                        ))}
                    </Stack>
                )}
                {existing.length === 0 && pendingFiles.length === 0 && (
                    <Typography sx={{ mt: 0.75, fontSize: 12, color: C.textMuted }}>
                        이미지는 미리보기로, 그 외 파일은 다운로드 링크로 표시됩니다.
                    </Typography>
                )}
            </FormField>
        </FormModal>
    );
}
